'use client';

import * as React from 'react';
import { Tooltip as TooltipPrimitive } from '@base-ui/react/tooltip';

import { cn } from '@/lib/utils';

const TooltipProvider = TooltipPrimitive.Provider;

const Tooltip = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Popup>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Popup> &
    Pick<
      TooltipPrimitive.Positioner.Props,
      'align' | 'alignOffset' | 'side' | 'sideOffset'
    >
>(
  (
    { className, align, alignOffset, side, sideOffset = 4, ...props },
    ref
  ) => (
    <TooltipPrimitive.Portal>
      {/* Positioner carries z-[1000] (not the convention's z-50): a tooltip
          opened inside a Dialog (backdrop/popup are z-[999]) must stack above
          it, and the Positioner is the fixed element that sets the tooltip
          subtree's stacking context. */}
      <TooltipPrimitive.Positioner
        className="isolate z-[1000]"
        align={align}
        alignOffset={alignOffset}
        side={side}
        sideOffset={sideOffset}
      >
        <TooltipPrimitive.Popup
          ref={ref}
          className={cn(
            // z-[1000] keeps tooltips above the Dialog layer (overlay/content are
            // z-[999]); at z-50 a tooltip opened inside a dialog rendered behind the
            // opaque panel and appeared not to open at all.
            'z-[1000] overflow-hidden rounded-xl border border-dashed border-ds-border-subtlest bg-ds-surface-white p-3 ds-text-body-xs-medium text-ds-text-darker shadow-[var(--ds-shadow-tooltip)] transition-[opacity,transform] data-starting-style:opacity-0 data-ending-style:opacity-0 data-starting-style:scale-95 data-ending-style:scale-95 data-[side=bottom]:data-starting-style:-translate-y-2 data-[side=left]:data-starting-style:translate-x-2 data-[side=right]:data-starting-style:-translate-x-2 data-[side=top]:data-starting-style:translate-y-2',
            className
          )}
          {...props}
        />
      </TooltipPrimitive.Positioner>
    </TooltipPrimitive.Portal>
  )
);
TooltipContent.displayName = TooltipPrimitive.Popup.displayName;

/**
 * Structured tooltip body: eyebrow, headline, then labelled rows.
 *
 * Lifted from the Incident Management tooltips (`incident-knowledge`
 * `TimelineTooltip`), which are the cleanest in the app and the pattern to copy.
 * The chrome was never the difference: `TooltipContent` already carries the same
 * dashed border, radius and shadow. What made those read well is the INTERNAL
 * shape. A quiet eyebrow says what you are pointing at, one semibold line gives
 * the answer, and everything else is a scannable label/value column instead of
 * a stack of sentences. Tooltips that stack full sentences make the reader parse
 * prose to find one number.
 *
 * Use for any data tooltip carrying more than a single phrase. A one-line
 * tooltip needs none of this: pass a string.
 */
function TooltipEyebrow({
  className,
  ...props
}: React.ComponentPropsWithoutRef<'div'>) {
  return (
    <div
      className={cn('ds-text-body-xs-medium mb-1 text-ds-text-subtle', className)}
      {...props}
    />
  );
}

function TooltipHeadline({
  className,
  ...props
}: React.ComponentPropsWithoutRef<'div'>) {
  return (
    <div
      className={cn(
        'ds-text-body-sm-semibold mb-1.5 text-ds-text-default',
        className
      )}
      {...props}
    />
  );
}

/**
 * One label/value line. `tone` colours the dot and the value together, so a
 * problem row is legible without a second icon: `danger` for a real fault,
 * `warning` for a nudge, `brand` for a healthy data value, `muted` (default)
 * for neutral detail. Omit `value` for a full-width note row.
 */
function TooltipRow({
  label,
  value,
  tone = 'muted',
  className,
}: {
  label: React.ReactNode;
  value?: React.ReactNode;
  tone?: 'muted' | 'brand' | 'warning' | 'danger';
  className?: string;
}) {
  const dot = {
    muted: 'bg-ds-gray-default',
    brand: 'bg-ds-brand-subtle',
    warning: 'bg-ds-warning-default',
    danger: 'bg-ds-danger-default',
  }[tone];
  const ink = {
    muted: 'text-ds-text-default',
    brand: 'text-ds-brand-subtle',
    warning: 'text-ds-warning-darker',
    danger: 'text-ds-danger-darker',
  }[tone];
  return (
    <div
      className={cn(
        'ds-text-body-xs-medium flex items-start gap-1.5 py-px',
        className
      )}
    >
      <span
        className={cn('mt-1 h-1.5 w-1.5 shrink-0 rounded-full', dot)}
        aria-hidden="true"
      />
      {/* A label/value row keeps its label quiet and lets the VALUE carry the
          tone, which is what makes a column of them scannable. A note row has
          no value, so the sentence itself has to carry it: left subtle, a
          fault message reads as ordinary detail and gets skipped. */}
      <span
        className={
          value == null && tone !== 'muted' ? ink : 'text-ds-text-subtle'
        }
      >
        {label}
      </span>
      {value != null && (
        <span className={cn('ml-auto shrink-0 pl-3 tabular-nums', ink)}>
          {value}
        </span>
      )}
    </div>
  );
}

export {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  TooltipEyebrow,
  TooltipHeadline,
  TooltipRow,
};
